import React, { useState } from 'react';
import { X, Plus, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { itemsAPI } from '../services/part2-api';
import { toast } from 'react-hot-toast';

/**
 * Quick Add Item Modal
 * Appears when scanned barcode is not found in inventory
 * Allows user to quickly create new item and auto-add to quotation
 */
const QuickAddItemModal = ({ barcode, onClose, onItemCreated }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    barcode: barcode || '',
    unit: 'PCS',
    selling_price: '',
    tax_rate: 18,
    hsn: '',
    item_type: 'product'
  });

  const [errors, setErrors] = useState({});

  const units = ['PCS', 'KG', 'LITER', 'METER', 'BOX', 'PAIR', 'SET'];
  const taxRates = [0, 5, 12, 18, 28];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name?.trim()) {
      newErrors.name = 'Item name is required';
    }
    
    if (!formData.selling_price || parseFloat(formData.selling_price) < 0) {
      newErrors.selling_price = 'Valid price is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      const itemData = {
        ...formData,
        selling_price: parseFloat(formData.selling_price),
        tax_rate: parseFloat(formData.tax_rate),
        status: 'active',
        track_inventory: false,
        stock_config: {
          track_stock: false,
          opening_stock: 0
        }
      };

      const response = await itemsAPI.create(itemData);
      toast.success('Item created successfully!');
      
      // Call parent callback with the new item
      if (onItemCreated) {
        onItemCreated(response.data);
      }
      
      onClose();
    } catch (error) {
      console.error('Failed to create item:', error);
      toast.error(error.response?.data?.detail || 'Failed to create item');
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <Card className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Plus className="w-6 h-6 text-blue-600" />
                Quick Add Item
              </h2>
              <p className="text-sm text-gray-600 mt-1">Item not found. Create it now and add to quotation.</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Alert */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3 mb-6">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <p className="font-semibold">Item not found with barcode: {barcode}</p>
              <p className="mt-1">Fill in the details below to create this item instantly.</p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Item Name */}
            <div>
              <Label htmlFor="name" className="text-sm font-semibold text-gray-700 mb-2 block">
                Item Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => updateFormData('name', e.target.value)}
                placeholder="Enter item name"
                className={errors.name ? 'border-red-500' : ''}
                autoFocus
              />
              {errors.name && <p className="text-red-600 text-xs mt-1">{errors.name}</p>}
            </div>

            {/* Barcode (readonly) */}
            <div>
              <Label htmlFor="barcode" className="text-sm font-semibold text-gray-700 mb-2 block">
                Barcode
              </Label>
              <Input
                id="barcode"
                type="text"
                value={formData.barcode}
                onChange={(e) => updateFormData('barcode', e.target.value)}
                placeholder="Barcode will be saved automatically"
                className="bg-gray-50"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Unit */}
              <div>
                <Label htmlFor="unit" className="text-sm font-semibold text-gray-700 mb-2 block">
                  Unit <span className="text-red-500">*</span>
                </Label>
                <select
                  id="unit"
                  value={formData.unit}
                  onChange={(e) => updateFormData('unit', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {units.map(unit => (
                    <option key={unit} value={unit}>{unit}</option>
                  ))}
                </select>
              </div>

              {/* Selling Price */}
              <div>
                <Label htmlFor="price" className="text-sm font-semibold text-gray-700 mb-2 block">
                  Selling Price <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={formData.selling_price}
                  onChange={(e) => updateFormData('selling_price', e.target.value)}
                  placeholder="0.00"
                  className={errors.selling_price ? 'border-red-500' : ''}
                />
                {errors.selling_price && <p className="text-red-600 text-xs mt-1">{errors.selling_price}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Tax Rate */}
              <div>
                <Label htmlFor="tax" className="text-sm font-semibold text-gray-700 mb-2 block">
                  Tax Rate (%)
                </Label>
                <select
                  id="tax"
                  value={formData.tax_rate}
                  onChange={(e) => updateFormData('tax_rate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {taxRates.map(rate => (
                    <option key={rate} value={rate}>{rate}%</option>
                  ))}
                </select>
              </div>

              {/* HSN Code */}
              <div>
                <Label htmlFor="hsn" className="text-sm font-semibold text-gray-700 mb-2 block">
                  HSN/SAC Code
                </Label>
                <Input
                  id="hsn"
                  type="text"
                  value={formData.hsn}
                  onChange={(e) => updateFormData('hsn', e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Create & Add to Quotation
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
};

export default QuickAddItemModal;
